/**
 * PasswordSection — In-App-Passwortänderung für angemeldete Kunden.
 *
 * Fund 25.08.2026: der einzige bisherige Weg war der öffentliche Forgot-Password-
 * E-Mail-Umweg (dafür ausloggen). Verlangt das aktuelle Passwort (POST
 * /auth/change-password); bei Erfolg entwertet der Server alle bestehenden
 * Sitzungen (password_updated_at), deshalb hier direkt ausloggen und zur
 * Anmeldung leiten, statt auf den nächsten 401 zu warten.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logout } from "@/lib/authToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LEN = 8;

export default function PasswordSection() {
  const { t } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < MIN_LEN) {
      toast.error(t(`Das neue Passwort muss mindestens ${MIN_LEN} Zeichen lang sein.`,
                    `The new password must be at least ${MIN_LEN} characters long.`));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("Die neuen Passwörter stimmen nicht überein.",
                    "The new passwords don't match."));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(t(
        "Passwort geändert. Du wirst aus Sicherheitsgründen abgemeldet — bitte melde dich neu an.",
        "Password changed. You'll be signed out for security — please sign in again.",
      ));
      await logout();
      window.location.assign("/login");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail
        || t("Passwort-Änderung fehlgeschlagen.", "Password change failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4" data-testid="password-section">
      <div className="flex items-center gap-2">
        <KeyRound size={20} className="text-stone-500" />
        <h2 className="font-heading font-bold text-lg text-stone-800">
          {t("Passwort ändern", "Change password")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
        <div>
          <label className="block text-sm text-stone-600 mb-1" htmlFor="current-password">
            {t("Aktuelles Passwort", "Current password")}
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            data-testid="current-password-input"
            className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-fox-fire outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-stone-600 mb-1" htmlFor="new-password">
            {t("Neues Passwort", "New password")}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={MIN_LEN}
            required
            data-testid="new-password-input"
            className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-fox-fire outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-stone-600 mb-1" htmlFor="confirm-password">
            {t("Neues Passwort bestätigen", "Confirm new password")}
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            minLength={MIN_LEN}
            required
            data-testid="confirm-password-input"
            className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-fox-fire outline-none text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="change-password-submit"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-fox-fire text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
          {t("Passwort ändern", "Change password")}
        </button>
      </form>
    </div>
  );
}
